import { ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CloudPayService } from '../cloud-pay.service';
import { BaseComponentDirective, BooleanObservableService } from '@streamamg/amg-common';
import { ActivatedRoute, ActivationEnd, Router } from '@angular/router';
import { filter, first } from 'rxjs/operators';
import { Location } from '@angular/common';

export const CLOUD_PAY_MODAL_KEY = 'modal-login';

export interface LoginTexts {
  fields: {
    email: string;
    password: string;
  };
  errors: {
    email: {
      required: string,
      pattern: string
    }
    password: {
      required: string;
      minLength: string;
    }
  };
  submit: string;
  forgotPassword: string;
  newAccount: string;
}

export class LoginModalDirective extends BaseComponentDirective {
  protected reloadOnLogin: boolean;
  public isOpened: boolean;
  public loginForm: FormGroup;
  public isLoading: boolean;
  protected passwordMinLength: number;
  public serverErrors: string[];
  // default values
  public texts: LoginTexts = {
    fields: {
      email: 'Email Address',
      password: 'Password',
    },
    errors: {
      email: {
        required: 'Email is required',
        pattern: 'Email not correct',
      },
      password: {
        required: 'Password is required',
        minLength: 'Password is too short',
      },
    },
    submit: 'Login',
    forgotPassword: 'Forgot Password?',
    newAccount: 'Don\'t you have an account yet? Create an account now!',
  };

  protected booleanObservableService = inject(BooleanObservableService);
  protected cloudPayService = inject(CloudPayService);
  protected formBuilder = inject(FormBuilder);
  protected router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  protected location = inject(Location);
  protected cd = inject(ChangeDetectorRef);

  constructor() {
    super();

    this.isLoading = false;
    this.isOpened = false;
    this.serverErrors = [];
  }

  protected init() {

    this.loginForm = this.formBuilder.group({
      emailaddress: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, Validators.minLength(this.passwordMinLength ? this.passwordMinLength : 6)]],
    });

    this.openModalBind();
  }

  protected openModalBind() {
    this.subSink = this.booleanObservableService.bind(CLOUD_PAY_MODAL_KEY)
        .subscribe((event) => {

          if (event === true) {
            // open modal
            this.isOpened = true;
            this.cd.markForCheck();

          } else {

            // close modal
            this.isOpened = false;
            this.resetForm();
            this.cd.detectChanges();
          }
        });

    this.showModal(this.activatedRoute);
    this.subSink = this.router.events
        .pipe(filter(event => event instanceof ActivationEnd))
        .subscribe((event) => {
          this.showModal((event as ActivationEnd));
        });
  }


  private showModal(event: ActivationEnd | ActivatedRoute): void {
    const login = event.snapshot.queryParams['login'] || event.snapshot.params['login'];

    if (login) {

      this.subSink = this.cloudPayService.cloudPayState().pipe(first()).subscribe((state) => {

        if (!state.isLoggedIn) {

          this.booleanObservableService.trigger(CLOUD_PAY_MODAL_KEY, login === 'true');
        }

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('login');
        this.location.replaceState(currentUrl.href.replace(currentUrl.origin, ''));
      });

    } else {

      this.closeModal();
    }
  }

  public closeModal(): void {
    this.booleanObservableService.trigger(CLOUD_PAY_MODAL_KEY, false);
  }

  protected resetForm() {
    this.loginForm.reset();
  }


  public isFieldInvalid(field: string) {
    return (!this.loginForm.get(field).valid && this.loginForm.get(field).touched) || this.loginForm.get(field).getError('server');
  }

  protected validateAllFormFields() {
    Object.keys(this.loginForm.controls).forEach(field => {
      const control = this.loginForm.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields();
      }
    });
  }

  public formSubmit(): void {

    if (this.loginForm.dirty && this.loginForm.valid) {
      this.isLoading = true;
      this.cd.markForCheck();

      const dataLogin = {
        emailaddress: this.loginForm.value.emailaddress,
        password: this.loginForm.value.password,
      };

      this.subSink = this.cloudPayService.login(dataLogin)
          .subscribe((res) => {
            this.serverErrors = [];

            if (!res.ModelErrors || !Object.keys(res.ModelErrors).length) {
              // success
              if (this.reloadOnLogin) {

                window.location.reload();

              } else {
                this.closeModal();
              }


            } else {
              Object.keys(res.ModelErrors).forEach(field => {

                if (this.loginForm.get(field)) {

                  this.loginForm.get(field).setErrors({server: res.ModelErrors[field]});

                } else {

                  this.serverErrors.push(res.ModelErrors[field]);
                }
              });
            }
            this.isLoading = false;
            this.cd.markForCheck();
          });
    } else {

      this.validateAllFormFields();
    }
  }

}
